import { B2BProducts, type StytchB2BUIConfig } from '@stytch/vanilla-js/b2b';
import { pill } from './console/components.ts';
import { esc, icon } from './console/format.ts';
import { SESSION_MINUTES } from './session.ts';

/**
 * How people log in to the console (features/identity-access/metodos-de-acceso.feature):
 * Google, email + password, and a code or link by email. Pure: main.ts mounts it.
 *
 * Stytch keeps and checks passwords (its strength policy and breach detection); Nubi never
 * sees one. Whatever the method, the session carries the member's own roles (ADR-0004).
 */

export { LOGIN_METHODS } from './login-methods.ts';

/** Minutes a «restablecer contraseña» link lasts: enough to open the mail, never a day. */
const RESET_MINUTES = 30;

/**
 * The faces the login paints (fonts.css): Lexend for the text (400) and for the headline and the
 * card's title (500); Lexend has no italic. The page is hidden until the app starts, so a font
 * that arrives after it shows swaps under the person's eyes and moves the card (CLS, #240).
 * build.ts preloads the file.
 */
export const LOGIN_FONTS = ['1em "Lexend Variable"', '500 1em "Lexend Variable"'] as const;

/** Never wait longer than this for them: a slow font is better than a hidden login. */
export const LOGIN_FONTS_WAIT_MS = 1000;

/** Resolves when the login's fonts are ready or after LOGIN_FONTS_WAIT_MS, never rejects. */
export function loginFonts(fonts: Pick<FontFaceSet, 'load'>): Promise<void> {
  const loaded = Promise.all(LOGIN_FONTS.map((f) => fonts.load(f))).then(
    () => undefined,
    () => undefined,
  );
  const late = new Promise<void>((resolve) => setTimeout(resolve, LOGIN_FONTS_WAIT_MS));
  return Promise.race([loaded, late]);
}

/**
 * Stytch's prebuilt B2B login (discovery: the person chooses their agency after proving who
 * they are). Google goes first; every redirect (Google, email links, password reset) comes back
 * to /auth/callback, where the same component finishes the login.
 */
export function stytchLoginConfig(origin: string): StytchB2BUIConfig {
  const back = `${origin}/auth/callback`;
  return {
    products: [
      B2BProducts.oauth,
      B2BProducts.emailOtp,
      B2BProducts.emailMagicLinks,
      B2BProducts.passwords,
    ],
    authFlowType: 'Discovery',
    sessionOptions: { sessionDurationMinutes: SESSION_MINUTES },
    // No One Tap: it needs Nubi's own Google client and loads Google's script into the page.
    oauthOptions: { providers: [{ type: 'google' }], discoveryRedirectURL: back },
    emailMagicLinksOptions: { discoveryRedirectURL: back },
    passwordOptions: {
      discoveryRedirectURL: back,
      resetPasswordRedirectURL: back,
      resetPasswordExpirationMinutes: RESET_MINUTES,
    },
  } as StytchB2BUIConfig;
}

/**
 * The same card in local and ephemeral environments, which log in through the identity
 * simulator (mocks/identity) instead of Stytch: each method is a page of the simulator that
 * returns to /auth/callback with a session of the chosen member. Links, not forms: the
 * console's CSP only lets forms post to its own origin.
 */
export function simulatorLogin(loginUrl: string, origin: string): string {
  const back = encodeURIComponent(`${origin}/auth/callback`);
  const at = (path: string) => {
    let url: string;
    try {
      url = new URL(path, loginUrl).toString();
    } catch {
      url = loginUrl;
    }
    return esc(`${url}?redirect_uri=${back}`);
  };
  return `<div class="card sim-box" data-component="login-methods">${pill('info', 'Ambiente de pruebas')}
      <p>Este ambiente usa el <strong>simulador de identidad</strong> en lugar de Stytch, con usuarios de prueba de agencias ficticias.</p>
      <div class="login-methods">
        <a class="btn lg" href="${at('oauth/google')}">${icon('google')}Continuar con Google</a>
        <p class="login-divider" aria-hidden="true"><span>o</span></p>
        <a class="btn lg" href="${at('password')}">${icon('lock')}Entrar con correo y contraseña</a>
        <a class="btn primary lg" href="${at('./')}">${icon('inbox')}Entrar con un usuario de prueba</a>
      </div>
      <p class="muted small">Con Stytch, la última opción es un código o un enlace a tu correo.</p></div>`;
}
